import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Route,
  Plus,
  Loader2,
  Power,
  Trash2,
  Building2,
  Zap,
  PlayCircle,
  CheckCircle2,
  XCircle,
  GitBranch,
  BarChart3,
  FlaskConical,
} from 'lucide-react';

export default function AdminRoutingPage() {
  const { api } = useAuth();
  const [rails, setRails] = useState([]);
  const [rules, setRules] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [simAmount, setSimAmount] = useState('');
  const [simIfsc, setSimIfsc] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [sandboxInput, setSandboxInput] = useState('50000, SBIN0001234\n5000, HDFC0001234\n120000');
  const [sandboxResults, setSandboxResults] = useState([]);
  const [sandboxLoading, setSandboxLoading] = useState(false);

  const [form, setForm] = useState({
    name: '',
    type: 'bank',
    account_identifier: '',
    priority: 1,
    daily_limit: 500000,
    provider: 'mock',
    notes: '',
  });

  const [ruleForm, setRuleForm] = useState({
    name: '',
    rule_type: 'ifsc_prefix',
    target_rail_id: '',
    ifsc_prefix: '',
    min_amount: '',
    max_amount: '',
    priority: 1,
  });

  const fetchData = useCallback(async () => {
    try {
      const [railsRes, rulesRes, insightsRes] = await Promise.all([
        api.get('/routing/rails'),
        api.get('/routing/rules'),
        api.get('/routing/insights'),
      ]);
      setRails(railsRes.data.rails || []);
      setRules(rulesRes.data.rules || []);
      setInsights(insightsRes.data);
    } catch (error) {
      toast.error('Failed to load routing config');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
      amount || 0
    );

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Rail name is required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/routing/rails', {
        ...form,
        priority: parseInt(form.priority, 10),
        daily_limit: parseFloat(form.daily_limit),
      });
      toast.success('Payout rail created');
      setShowDialog(false);
      setForm({ name: '', type: 'bank', account_identifier: '', priority: 1, daily_limit: 500000, provider: 'mock', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rail');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rail) => {
    try {
      await api.post(`/routing/rails/${rail.id}/toggle`);
      toast.success(`${rail.name} ${rail.status === 'active' ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle rail');
    }
  };

  const handleDelete = async (rail) => {
    try {
      await api.delete(`/routing/rails/${rail.id}`);
      toast.success('Rail deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rail');
    }
  };

  const handleCreateRule = async () => {
    if (!ruleForm.name.trim() || !ruleForm.target_rail_id) {
      toast.error('Rule name and target rail are required');
      return;
    }
    if (ruleForm.rule_type === 'ifsc_prefix' && !ruleForm.ifsc_prefix.trim()) {
      toast.error('IFSC prefix is required');
      return;
    }
    setSavingRule(true);
    try {
      const payload = {
        name: ruleForm.name,
        rule_type: ruleForm.rule_type,
        target_rail_id: ruleForm.target_rail_id,
        priority: parseInt(ruleForm.priority, 10),
      };
      if (ruleForm.rule_type === 'ifsc_prefix') {
        payload.ifsc_prefix = ruleForm.ifsc_prefix.trim().toUpperCase();
      } else {
        payload.min_amount = ruleForm.min_amount ? parseFloat(ruleForm.min_amount) : 0;
        payload.max_amount = ruleForm.max_amount ? parseFloat(ruleForm.max_amount) : 0;
      }
      await api.post('/routing/rules', payload);
      toast.success('Routing rule created');
      setShowRuleDialog(false);
      setRuleForm({ name: '', rule_type: 'ifsc_prefix', target_rail_id: '', ifsc_prefix: '', min_amount: '', max_amount: '', priority: 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    } finally {
      setSavingRule(false);
    }
  };

  const handleToggleRule = async (rule) => {
    try {
      await api.post(`/routing/rules/${rule.id}/toggle`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const handleDeleteRule = async (rule) => {
    try {
      await api.delete(`/routing/rules/${rule.id}`);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleSimulate = async () => {
    const amount = parseFloat(simAmount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSimLoading(true);
    setSimResult(null);
    try {
      const params = new URLSearchParams({ amount: String(amount) });
      if (simIfsc.trim()) params.append('ifsc_code', simIfsc.trim().toUpperCase());
      const res = await api.post(`/routing/simulate?${params.toString()}`);
      setSimResult(res.data);
    } catch (error) {
      toast.error('Simulation failed');
    } finally {
      setSimLoading(false);
    }
  };

  const handleSandbox = async () => {
    const items = sandboxInput
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [amountStr, ifsc] = line.split(',').map((s) => s.trim());
        return { amount: parseFloat(amountStr), ifsc_code: ifsc || null };
      })
      .filter((it) => it.amount && it.amount > 0);

    if (items.length === 0) {
      toast.error('Enter at least one valid "amount, IFSC" line');
      return;
    }
    setSandboxLoading(true);
    setSandboxResults([]);
    try {
      const res = await api.post('/routing/simulate-batch', { items });
      setSandboxResults(res.data.results || []);
    } catch (error) {
      toast.error('Batch simulation failed');
    } finally {
      setSandboxLoading(false);
    }
  };

  const ruleCondition = (rule) => {
    if (rule.rule_type === 'ifsc_prefix') return `IFSC starts with ${rule.ifsc_prefix}`;
    const lo = rule.min_amount || 0;
    const hi = rule.max_amount;
    return hi ? `Amount ₹${lo.toLocaleString('en-IN')} – ₹${hi.toLocaleString('en-IN')}` : `Amount ≥ ₹${lo.toLocaleString('en-IN')}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Route className="w-6 h-6" />
            Multi-Bank Routing
          </h1>
          <p className="text-muted-foreground">Configure payout rails, routing rules and preview decisions</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="add-rail-btn">
          <Plus className="mr-2 h-4 w-4" />
          Add Rail
        </Button>
      </div>

      {/* Routing Simulator */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="w-4 h-4 text-yellow-500" />
            Routing Simulator
          </CardTitle>
          <CardDescription>Preview which rail an amount + IFSC would be routed to</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[180px]">
              <Label htmlFor="sim-amount">Payout Amount (INR)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                <Input
                  id="sim-amount"
                  type="number"
                  placeholder="50000"
                  value={simAmount}
                  onChange={(e) => setSimAmount(e.target.value)}
                  className="pl-8"
                  data-testid="sim-amount-input"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[160px]">
              <Label htmlFor="sim-ifsc">Beneficiary IFSC (optional)</Label>
              <Input
                id="sim-ifsc"
                placeholder="e.g., SBIN0001234"
                value={simIfsc}
                onChange={(e) => setSimIfsc(e.target.value)}
                className="mt-1 uppercase"
                data-testid="sim-ifsc-input"
              />
            </div>
            <Button onClick={handleSimulate} disabled={simLoading} data-testid="simulate-btn">
              {simLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="mr-2 h-4 w-4" />
              )}
              Simulate
            </Button>
          </div>

          {simResult && (
            <div
              className={`mt-4 p-4 rounded-lg border ${
                simResult.routed ? 'bg-green-500/5 border-green-500/30' : 'bg-red-500/5 border-red-500/30'
              }`}
              data-testid="sim-result"
            >
              <div className="flex items-center gap-2 mb-1">
                {simResult.routed ? (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <p className="font-medium">
                  {simResult.routed ? `Routed to ${simResult.selected_rail?.name}` : 'No rail available'}
                </p>
                {simResult.matched_rule && (
                  <Badge variant="secondary" className="text-[10px]">
                    via rule: {simResult.matched_rule.name}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">{simResult.reason}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rule Testing Sandbox */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="w-4 h-4 text-purple-500" />
            Rule Testing Sandbox
          </CardTitle>
          <CardDescription>
            Batch-preview routing for many payouts. One per line: <code>amount, IFSC</code> (IFSC optional)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            rows={4}
            value={sandboxInput}
            onChange={(e) => setSandboxInput(e.target.value)}
            className="font-mono text-sm"
            placeholder={'50000, SBIN0001234\n5000, HDFC0001234\n120000'}
            data-testid="sandbox-input"
          />
          <Button onClick={handleSandbox} disabled={sandboxLoading} data-testid="sandbox-run-btn">
            {sandboxLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
            Run Batch
          </Button>

          {sandboxResults.length > 0 && (
            <div className="overflow-x-auto" data-testid="sandbox-results">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-3">Amount</th>
                    <th className="text-left py-2 px-3">IFSC</th>
                    <th className="text-left py-2 px-3">Routed Rail</th>
                    <th className="text-left py-2 px-3">Matched Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {sandboxResults.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 px-3 font-mono">{formatCurrency(r.amount)}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.ifsc_code || '—'}</td>
                      <td className="py-2 px-3">
                        {r.routed ? (
                          <span className="font-medium">{r.rail}</span>
                        ) : (
                          <span className="text-red-500">No rail</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {r.matched_rule ? (
                          <Badge variant="secondary" className="text-[10px]">{r.matched_rule}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">priority fallback</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Routing Rules */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <GitBranch className="w-4 h-4" />
              Routing Rules ({rules.length})
            </CardTitle>
            <CardDescription>Evaluated by priority before the default fallback</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRuleDialog(true)}
            disabled={rails.length === 0}
            data-testid="add-rule-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Rule
          </Button>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No routing rules — payouts use priority fallback across active rails
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                  data-testid={`rule-${rule.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="text-[10px]">
                      P{rule.priority}
                    </Badge>
                    <div>
                      <p className="font-medium text-sm">{rule.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {ruleCondition(rule)} → <span className="font-medium">{rule.target_rail_name}</span>
                      </p>
                    </div>
                    <Badge
                      className={
                        rule.enabled
                          ? 'bg-green-100 text-green-700 border-green-300 border text-[10px]'
                          : 'bg-gray-100 text-gray-600 border-gray-300 border text-[10px]'
                      }
                    >
                      {rule.enabled ? 'enabled' : 'disabled'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleToggleRule(rule)} data-testid={`toggle-rule-${rule.id}`}>
                      <Power className={`h-4 w-4 ${rule.enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule)} data-testid={`delete-rule-${rule.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rails List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payout Rails ({rails.length})</CardTitle>
          <CardDescription>Rails are evaluated by priority (lowest number first)</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : rails.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Route className="w-12 h-12 mx-auto opacity-50 mb-4" />
              <p>No payout rails configured</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rails.map((rail) => {
                const usedPct = Math.min(100, ((rail.daily_used || 0) / (rail.daily_limit || 1)) * 100);
                return (
                  <div
                    key={rail.id}
                    className="p-4 rounded-lg border hover:bg-muted/40 transition-colors"
                    data-testid={`rail-${rail.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {rail.type === 'gateway' ? (
                            <Zap className="w-5 h-5 text-primary" />
                          ) : (
                            <Building2 className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{rail.name}</p>
                            <Badge variant="secondary" className="text-[10px]">
                              Priority {rail.priority}
                            </Badge>
                            <Badge
                              className={
                                rail.status === 'active'
                                  ? 'bg-green-100 text-green-700 border-green-300 border text-[10px]'
                                  : 'bg-gray-100 text-gray-600 border-gray-300 border text-[10px]'
                              }
                            >
                              {rail.status}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] uppercase">
                              {rail.provider || 'mock'}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                            {rail.type} • {rail.account_identifier || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggle(rail)} data-testid={`toggle-rail-${rail.id}`}>
                          <Power className={`h-4 w-4 ${rail.status === 'active' ? 'text-green-500' : 'text-muted-foreground'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(rail)} data-testid={`delete-rail-${rail.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-muted-foreground mb-1">
                        <span>
                          Used {formatCurrency(rail.daily_used)} / {formatCurrency(rail.daily_limit)}
                        </span>
                        <span>{formatCurrency(rail.daily_remaining)} remaining</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${usedPct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Failover Insights */}
      <Card data-testid="failover-insights">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-4 h-4 text-blue-500" />
            Failover Insights
          </CardTitle>
          <CardDescription>
            {insights
              ? `${insights.total_attempts} execution attempts • ${insights.total_failovers} rail failures • ${insights.payouts_with_failover} payouts needed failover`
              : 'How often each rail succeeds vs is bypassed'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!insights || insights.rails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No executions yet — run "Auto Execute" on approved payouts to gather data
            </div>
          ) : (
            <div className="space-y-3">
              {insights.rails.map((r) => (
                <div key={r.rail_id} className="p-3 rounded-lg border" data-testid={`insight-${r.rail_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm">{r.rail_name}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-green-500">{r.successes} ok</span>
                      <span className="text-red-500">{r.failures} failed</span>
                      <Badge variant="secondary" className="text-[10px]">
                        {r.failure_rate}% fail
                      </Badge>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 flex overflow-hidden">
                    <div className="bg-green-500 h-2" style={{ width: `${r.attempts ? (r.successes / r.attempts) * 100 : 0}%` }} />
                    <div className="bg-red-500 h-2" style={{ width: `${r.attempts ? (r.failures / r.attempts) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Rail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Payout Rail</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rail-name">Rail Name</Label>
              <Input
                id="rail-name"
                placeholder="e.g., Axis Bank Rail"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="rail-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="rail-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="gateway">Gateway</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Provider (Executor)</Label>
                <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
                  <SelectTrigger data-testid="rail-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mock">Mock (default)</SelectItem>
                    <SelectItem value="razorpayx">RazorpayX (stub)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rail-priority">Priority</Label>
                <Input
                  id="rail-priority"
                  type="number"
                  min="0"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  data-testid="rail-priority-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rail-limit">Daily Limit (INR)</Label>
                <Input
                  id="rail-limit"
                  type="number"
                  value={form.daily_limit}
                  onChange={(e) => setForm({ ...form, daily_limit: e.target.value })}
                  data-testid="rail-limit-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rail-account">Account / Gateway Identifier</Label>
              <Input
                id="rail-account"
                placeholder="e.g., XXXXXX3003 or gw_id"
                value={form.account_identifier}
                onChange={(e) => setForm({ ...form, account_identifier: e.target.value })}
                data-testid="rail-account-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rail-notes">Notes (Optional)</Label>
              <Textarea
                id="rail-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-rail-btn">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Create Rail
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Routing Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rule-name">Rule Name</Label>
              <Input
                id="rule-name"
                placeholder="e.g., Route SBI to ICICI"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                data-testid="rule-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rule Type</Label>
                <Select value={ruleForm.rule_type} onValueChange={(v) => setRuleForm({ ...ruleForm, rule_type: v })}>
                  <SelectTrigger data-testid="rule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ifsc_prefix">IFSC Prefix</SelectItem>
                    <SelectItem value="amount_band">Amount Band</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rule-priority">Priority</Label>
                <Input
                  id="rule-priority"
                  type="number"
                  min="1"
                  value={ruleForm.priority}
                  onChange={(e) => setRuleForm({ ...ruleForm, priority: e.target.value })}
                  data-testid="rule-priority-input"
                />
              </div>
            </div>

            {ruleForm.rule_type === 'ifsc_prefix' ? (
              <div className="space-y-2">
                <Label htmlFor="rule-ifsc">IFSC Prefix</Label>
                <Input
                  id="rule-ifsc"
                  placeholder="e.g., SBIN or HDFC"
                  value={ruleForm.ifsc_prefix}
                  onChange={(e) => setRuleForm({ ...ruleForm, ifsc_prefix: e.target.value })}
                  className="uppercase"
                  data-testid="rule-ifsc-input"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rule-min">Min Amount (₹)</Label>
                  <Input
                    id="rule-min"
                    type="number"
                    placeholder="0"
                    value={ruleForm.min_amount}
                    onChange={(e) => setRuleForm({ ...ruleForm, min_amount: e.target.value })}
                    data-testid="rule-min-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rule-max">Max Amount (₹)</Label>
                  <Input
                    id="rule-max"
                    type="number"
                    placeholder="No limit"
                    value={ruleForm.max_amount}
                    onChange={(e) => setRuleForm({ ...ruleForm, max_amount: e.target.value })}
                    data-testid="rule-max-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Target Rail</Label>
              <Select value={ruleForm.target_rail_id} onValueChange={(v) => setRuleForm({ ...ruleForm, target_rail_id: v })}>
                <SelectTrigger data-testid="rule-target-select">
                  <SelectValue placeholder="Select rail" />
                </SelectTrigger>
                <SelectContent>
                  {rails.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateRule} disabled={savingRule} data-testid="save-rule-btn">
              {savingRule ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Create Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
